import { Drawer } from "app/components/ui/drawer/Drawer";
import { Fragment, memo, useCallback, useEffect, useRef, useState } from "react";
import { useField } from "formik";
import classNames from "classnames";
import PropTypes from "prop-types";
import { messagePropType } from "app/utils/propTypes";
import AdvancedSelectFormik from "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik";
import "./SmartDPInputSidePanelContent.scss";
import isEmpty from "lodash/isEmpty";
import useHidePartnerCustomHeader from "app/utils/hooks/useHidePartnerCustomHeader";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import useBlockScroll from "app/utils/hooks/useBlockScroll";
import useReadyOnTransitionEnd from "app/utils/hooks/useReadyOnTransitionEnd";

const DRAWER_ANIMATION_DURATION_MS = 200;

// Filet de sécurité si transitionend ne se déclenche jamais. Valeur volontairement large et fixe,
// pas un multiple de la durée nominale (qui pourrait se déclencher trop tôt sur un appareil lent).
const MENU_READY_FALLBACK_MS = 2000;

const SmartDPInputSidePanelContent = props => {
	const {
		id,
		label,
		className,
		isRequired,
		drawerInputLabel,
		drawerTitleLabel,
		getOptionLabel = () => {},
		isSearchable = true,
		selectRef: externalSelectRef,
		value: explicitValue,
	} = props;
	const [isOpen, setOpen] = useState(false);
	// react-select ne calcule la position fixe de son menu qu'une fois, à l'ouverture. S'il s'ouvre
	// avant la fin du glissement du tiroir, il se positionne hors écran et n'est jamais recalculé.
	const [handleDrawerContentRef, isMenuReady, resetMenuReady] = useReadyOnTransitionEnd(
		"transform",
		MENU_READY_FALLBACK_MS
	);
	const [field, meta, helpers] = useField(props);
	const { setError } = helpers;
	const selectRef = useRef();
	useBlockScroll(isOpen);
	useHidePartnerCustomHeader({ hide: isOpen });

	useEffect(() => {
		if (!isOpen) {
			resetMenuReady();
		}
	}, [isOpen, resetMenuReady]);

	const displayValue = explicitValue !== undefined ? explicitValue : field.value;
	const isTouched = displayValue;

	const inputClassName = classNames("smartdp-input-search-select", className, {
		"smartdp-input-search-select--required": isRequired,
		"smartdp-input-search-select--touched": isTouched,
		"smartdp-input-search-select--error": meta.touched && meta.error,
	});

	const handleInputValueChange = useCallback(option => {
		helpers.setValue(option);
		helpers.setTouched(true);
		if (!isEmpty(option)) {
			handleOpenChange(false);
		}
	}, []);

	const handleOpenChange = useCallback(isOpen => {
		if (isOpen) {
			setOpen(true);
			setError();
		} else {
			setOpen(false);
		}
	}, []);

	// Ouvre le tiroir depuis l'extérieur via un setState sur ce composant déjà monté, pas en le
	// montant déjà ouvert (les refs internes ne seraient pas encore prêtes).
	useEffect(() => {
		if (!externalSelectRef) {
			return undefined;
		}

		externalSelectRef.current = { open: () => handleOpenChange(true) };

		return () => {
			externalSelectRef.current = null;
		};
	}, [externalSelectRef, handleOpenChange]);

	return (
		<Fragment>
			<button
				type="button"
				className={inputClassName}
				onClick={() => handleOpenChange(true)}
				data-testId={props["data-testid"]}
			>
				<label htmlFor={id} className="smartdp-input-search-select__label">
					<Typography
						className="smartdp-input-search-select__label-text"
						variant={
							isTouched ? TYPOGRAPHY_VARIANTS.XSMALL : TYPOGRAPHY_VARIANTS.REGULAR
						}
					>
						{label}
					</Typography>
				</label>
				{displayValue && (
					<div className="smartdp-input-search-select__input">
						{getOptionLabel(displayValue)}
					</div>
				)}
			</button>

			<Drawer.Root
				open={isOpen}
				onOpenChange={handleOpenChange}
				className="smartDP-input-side-panel"
				animationDuration={DRAWER_ANIMATION_DURATION_MS}
			>
				<Drawer.Content
					className="smartDP-input-side-panel__content"
					contentRef={handleDrawerContentRef}
				>
					<Drawer.Header>
						<Typography variant={TYPOGRAPHY_VARIANTS.XL} isBold>
							{drawerTitleLabel?.length > 0 ? drawerTitleLabel : label}
						</Typography>
					</Drawer.Header>

					<Drawer.Body>
						<AdvancedSelectFormik
							menuIsOpen={isMenuReady}
							{...props}
							label={drawerInputLabel ? drawerInputLabel : label}
							autoFocus={false}
							isClearable={isSearchable}
							selectRef={selectRef}
							classNamePrefix="advanced-select__select-sdp"
							onChange={handleInputValueChange}
							captureMenuScroll={false}
							menuPortalTarget={null} // mode portal par défaut, on force à null pour éviter pb affichage sur mobile quand le side panel s'ouvre
						/>
					</Drawer.Body>
				</Drawer.Content>
			</Drawer.Root>
		</Fragment>
	);
};

SmartDPInputSidePanelContent.propTypes = {
	id: PropTypes.string,
	label: messagePropType,
	value: PropTypes.any,
	"data-testid": PropTypes.string,
	isSearchable: PropTypes.bool,
	getOptionLabel: PropTypes.func,
	isRequired: PropTypes.bool,
	drawerInputLabel: messagePropType,
	drawerTitleLabel: messagePropType,
	selectRef: PropTypes.object,
};

export default memo(SmartDPInputSidePanelContent);
